import { SelectOption } from 'components/inputs/BaseSelect';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import LoadingOverlay from 'components/Overlays/LoadingOverlay';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlaySection from 'components/Overlays/OverlaySection';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import { toastText } from 'copy/copy';
import Immutable from 'immutable';
import React, { useState } from 'react';
import { AssignSubmittal } from 'records/interactions/AssignSubmittal';
import { ViewSubmittal } from 'records/interactions/ViewSubmittal';
import { Project } from 'records/Project';
import { Submittal } from 'records/Submittal';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator, useDateFormatter } from '@bluebeam/react-i18n';
import { SubmittalStatus } from 'records/SubmittalStatus';
import SubmittalAttachmentButtons from '../SubmittalAttachmentButtons';

interface Props extends WithAppStoreProps {
  interaction: AssignSubmittal;
}

const AssignSubmittalOverlay = (props: Props) => {
  const { appState, actions, interaction } = props;
  const { submittalStatuses } = appState;
  const { translate } = useTranslator([
    'submittalsPage',
    'buttons',
    'statusLabels',
    'formLabels',
  ]);

  const dateFormatter = useDateFormatter({
    dateStyle: 'medium',
  });

  const [updatingSubmittal, setUpdatingSubmittal] = useState<boolean>(false);
  const [nextSubmittal, setNextSubmittal] = useState<Submittal>(
    props.interaction.submittal.delete('status').delete('ballincourt')
  );

  const openViewSubmittalOverlay = async (
    submittal: Submittal
  ): Promise<void> => {
    const viewSubInteraction = new ViewSubmittal({
      submittal,
    });

    await actions.clearInteractions();

    actions.createInteraction(viewSubInteraction);
  };

  const handleCloseOverlay = (): void => {
    actions.clearInteractions();
  };

  const handleCancelClick = async (): Promise<void> => {
    openViewSubmittalOverlay(props.interaction.submittal);
  };

  const handleAddFile = (additionalFiles: FileList): void => {
    handleRemoveClick();

    const files = [...Array.from(additionalFiles)].map((file): File => file);

    const nextFiles = Immutable.List(files);

    const updatedInteraction = interaction.set('files', nextFiles);

    actions.updateInteraction(updatedInteraction);
  };

  const handleRemoveClick = async (): Promise<void> => {
    const nextFiles = Immutable.List();

    const updatedInteraction = interaction.set('files', nextFiles);

    actions.updateInteraction(updatedInteraction);
  };

  const handleSubmittalStatusChange = (kind: string): void => {
    const status = submittalStatuses.find((ss) => ss.kind === kind);

    if (status) {
      setNextSubmittal(nextSubmittal.set('status', status));
    }
  };

  const handleAssigneeChange = (assignee: string): void => {
    setNextSubmittal(nextSubmittal.set('ballincourt', assignee));
  };

  const saveDisabled = (): boolean => {
    const subRequested =
      nextSubmittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      nextSubmittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    return (
      nextSubmittal.status.kind === '' ||
      nextSubmittal.ballincourt === '' ||
      (subRequested && nextSubmittal.duefromsub === 0) ||
      (subDesignReview && nextSubmittal.duefromreviewer === 0)
    );
  };

  const handleAssignSubmittal = async (): Promise<void> => {
    try {
      setUpdatingSubmittal(true);
      const newState = await actions.updateSubmittal(
        nextSubmittal,
        interaction.files
      );
      const { submittals } = newState;
      const updatedSubmittal = submittals.get(nextSubmittal.id, nextSubmittal);
      await openViewSubmittalOverlay(updatedSubmittal);
    } catch (err) {
      console.error('Unable to assign submittal', err);
      toast.error(
        translate('toastText:submittalAssignError.message'),
        translate('toastText:submittalAssignError.title')
      );
    }
  };

  const assigneesToSelectOptions = (): SelectOption[] => {
    const { projects, selectedProject } = appState;
    const project = projects.get(selectedProject, new Project());

    return project.assignees.map((a) => ({ label: a, value: a })).toArray();
  };

  const handleDueDateChanged = (date: Date): void => {
    const subRequested =
      nextSubmittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      nextSubmittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    if (subRequested) {
      setNextSubmittal(
        nextSubmittal.set('duefromsub', Math.floor(date.getTime() / 1000))
      );
    }

    if (subDesignReview) {
      setNextSubmittal(
        nextSubmittal.set('duefromreviewer', Math.floor(date.getTime() / 1000))
      );
    }
  };

  const getDueDate = (): number | null => {
    const subRequested =
      nextSubmittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      nextSubmittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    if (subRequested && nextSubmittal.duefromsub > 0) {
      return nextSubmittal.duefromsub;
    }

    if (subDesignReview && nextSubmittal.duefromreviewer > 0) {
      return nextSubmittal.duefromreviewer;
    }

    return null;
  };

  const duedateDisabled = (): boolean => {
    const subRequested =
      nextSubmittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      nextSubmittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    return !subRequested && !subDesignReview;
  };

  const { submittal } = interaction;

  const translatedStatuses = submittalStatuses.map((status) => {
    const categoryType: `${string}:${string}` =
      `statusLabels:submittals.${status.kind}` as `${string}:${string}`;

    return new SubmittalStatus({
      kind: status.kind,
      display: translate(categoryType),
    });
  });

  const statuses = translatedStatuses.filter(
    (s) =>
      s.kind === 'SUB_SUBMITTAL_REQUESTED' ||
      s.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW'
  );

  const categoryType: `${string}:${string}` =
    `submittalsPage:categories.${submittal.category.kind}` as `${string}:${string}`;

  return (
    <OverlayStdContainer
      id="assign-submittal-overlay"
      style={{ backgroundColor: 'var(--white-2)' }}
    >
      {updatingSubmittal ? (
        <LoadingOverlay text={translate('submittalsPage:assigningSubmittal')} />
      ) : null}
      <OverlayHeader
        id="assign-submittal-overlay-header"
        title={`${translate('submittalsPage:submittalNumber')}: ${
          nextSubmittal.specsectionnumber
        }-${nextSubmittal.sectioncount.toString().padStart(3, '0')}`}
        onCancelClick={handleCloseOverlay}
      />
      <div className="column justify-between oa grow-1">
        <div className="mx4 my2">
          <OverlaySection label={translate('buttons:assign')}>
            <div className="mb3">
              <Label label={translate('statusLabels:status')} required />
              <SingleSelect
                id="assign-submittal-overlay-status"
                placeholder={translate('formLabels:selectWithDots')}
                value={translatedStatuses.reduce<SelectOption | null>(
                  (acc, sc) => {
                    if (sc.kind !== nextSubmittal.status.kind) {
                      return acc;
                    }

                    return sc.toSelectOption();
                  },
                  null
                )}
                options={statuses.map((sc) => sc.toSelectOption()).toArray()}
                onChange={(sc: SelectOption): void => {
                  handleSubmittalStatusChange(sc.value);
                }}
              />
            </div>
            <div className="mb3">
              <Label label={translate('formLabels:assignee')} required />
              <SingleSelect
                id="assign-submittal-overlay-assignee"
                value={{
                  label: nextSubmittal.ballincourt,
                  value: nextSubmittal.ballincourt,
                }}
                options={assigneesToSelectOptions()}
                onChange={(sc): void => {
                  handleAssigneeChange(sc.value);
                }}
              />
            </div>
            <div className="mb4">
              <Label label={translate('formLabels:duedate')} required />
              <DateDropdown
                id="assign-submittal-duedate"
                date={getDueDate()}
                onChange={handleDueDateChanged}
                disabled={duedateDisabled()}
              />
            </div>
            <SubmittalAttachmentButtons
              id="assign-submittal-sub-attach-btns"
              submittal={nextSubmittal}
              files={interaction.files}
              attachButtonTitle={translate('submittalsPage:attachOptional')}
              onFileSelect={handleAddFile}
              onRemoveClick={handleRemoveClick}
            />
          </OverlaySection>
          <OverlaySection label={translate('submittalsPage:generalDetails')}>
            <div className="mb3">
              <Label label={`${translate('submittalsPage:specNumberFull')}:`} />
              <div className="light font-size-5 color-gray-7">
                {submittal.specsectionnumber}
              </div>
            </div>
            <div className="mb3">
              <Label label={`${translate('submittalsPage:specSection')}:`} />
              <div className="light font-size-5 color-gray-7 min-hs1">
                {submittal.specsectionname}
              </div>
            </div>
            <div className="mb3">
              <Label
                label={`${translate('submittalsPage:submittalCategory')}:`}
              />
              <div className="light font-size-5 color-gray-7">
                {translate(categoryType)}
              </div>
            </div>
            <div className="mb3">
              <Label label={`${translate('submittalsPage:description')}:`} />
              <div className="light font-size-5 color-gray-7">
                {submittal.description}
              </div>
            </div>
            <div className="mb3">
              <Label
                label={`${translate('submittalsPage:responsibleCompany')}:`}
              />
              <div className="light font-size-5 color-gray-7">
                {submittal.responsiblecompany}
              </div>
            </div>
            <div className="row mb3">
              <div className="w50 mr3">
                <Label label={`${translate('submittalsPage:dueSubmitter')}:`} />
                <div className="light font-size-5 color-gray-7">
                  {submittal.duefromsub
                    ? dateFormatter.format(
                        new Date(submittal.duefromsub * 1000)
                      )
                    : translate('submittalsPage:noDueDate')}
                </div>
              </div>
              <div className="w50">
                <Label label={`${translate('submittalsPage:dueReviewer')}:`} />
                <div className="light font-size-5 color-gray-7">
                  {submittal.duefromreviewer
                    ? dateFormatter.format(
                        new Date(submittal.duefromreviewer * 1000)
                      )
                    : translate('submittalsPage:noDueDate')}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="w50 mr3">
                <Label label={`${translate('submittalsPage:leadTime')}:`} />
                <div className="light font-size-5 color-gray-7">
                  {submittal.leadtime}
                </div>
              </div>
              <div className="w50">
                <Label
                  label={`${translate('submittalsPage:requiredOnSite')}:`}
                />
                <div className="light font-size-5 color-gray-7">
                  {submittal.requiredonsite
                    ? dateFormatter.format(
                        new Date(submittal.requiredonsite * 1000)
                      )
                    : translate('submittalsPage:noDueDate')}
                </div>
              </div>
            </div>
          </OverlaySection>
        </div>
      </div>
      <OverlayFooter
        id="assign-submittal-overlay-footer"
        confirmText={translate('buttons:send')}
        onCancelClick={handleCancelClick}
        onConfirmClick={handleAssignSubmittal}
        confirmDisabled={saveDisabled()}
      />
    </OverlayStdContainer>
  );
};

export default withAppStore<Props>(AssignSubmittalOverlay);
